/**
 * AtlasForecastModule — the Town window's "Weather" tab: the Commons forecast at the player's spot (sweep 2026-10-05).
 * Rules + the sampler in lib/weather_forecast.ts (it calls the renderer's own resolver at a later clock).
 *   · now · +1 h · +3 h · +6 h: kind, intensity, wind, temperature, cloud cover, and a storm-hazard warning (a
 *     thunderstorm / blizzard / sandstorm by lib/storm_hazards.ts's thresholds);
 *   · a PINNED weather (the live-ops sky row, or the debug pin) is reported as held by the weather office — never a
 *     forecast of the natural field it has overridden;
 *   · off the Commons: "no forecast here";
 *   · refreshed at most every 30 s while the tab is open, by SOFT RENDER (a focused control is never replaced).
 * THE TOKEN LAW: `--ax-*` tokens and the shared `ax-*` classes only. Lever `__forecast()`.
 */
import type { App, IModule } from 'enginex';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { pickWeatherOverride } from '../lib/weather.js';
import { currentTod } from './SkyModule.js';
import { FORECAST_REFRESH_MS, DEFAULT_CYCLE_S, firstHazard, forecast, percentText, tempText, type ForecastResult, type ForecastSlot } from '../lib/weather_forecast.js';
import { softRender } from '../lib/soft_render.js';
import { el, head, muted } from './town/town_ui.js';
import { registerTownTab } from './town/town_tabs.js';

const KIND_WORDS: Record<string, string> = { clear: 'Clear', cloudy: 'Cloudy', fog: 'Fog', rain: 'Rain', storm: 'Thunderstorm', snow: 'Snow', sandstorm: 'Sandstorm' };

export class AtlasForecastModule implements IModule {
  readonly name = 'AtlasForecast';
  private app: App | null = null;
  private tabEl: HTMLElement | null = null;
  private lastRender = 0;
  private renders = 0;
  private last: ForecastResult | null = null;
  private lastNote = '';
  /** Probe lever only: ignore every pin so the natural field renders. */
  private ignorePins = false;

  init(app: App): void {
    this.app = app;
    // order 1: beside Festivals (0) and the posts (2) — the tabs wrap, so one more costs nothing.
    registerTownTab({ key: 'weather', label: 'Weather', order: 1, render: (h) => { this.tabEl = h; this.draw(true); } });
    app.world.addSystem(makeSystem('ForecastRefresh', Phase.UI, 60, () => this.tick()));
    (window as unknown as Record<string, unknown>).__forecast = () => ({ renders: this.renders, note: this.lastNote, open: !!this.tabEl?.isConnected, last: this.last, compute: () => this.compute(), natural: (on: boolean) => { this.ignorePins = on; this.draw(true); } });
  }

  private tick(): void {
    // Detached when another tab is picked; zero client rects when the window is closed (its body stays in the DOM).
    if (!this.tabEl?.isConnected || this.tabEl.getClientRects().length === 0) return;
    if (performance.now() - this.lastRender >= FORECAST_REFRESH_MS) this.draw(false);
  }

  /** The forecast for the player's spot, or a note why there is none. */
  private compute(): { result: ForecastResult | null; note: string } {
    const conn = atlas.conn;
    if (!conn) return { result: null, note: 'Not connected.' };
    const commons = atlas.commonsWorldId;
    if (atlas.currentWorldId === null || commons === null || atlas.currentWorldId !== commons) return { result: null, note: 'No forecast here — the weather office covers the Commons only.' };
    let seed = 'commons', cycle = DEFAULT_CYCLE_S, skyWeather: string | undefined, held = false;
    try {
      const w = conn.db.world.worldId.find(commons);
      if (w) seed = w.terrainSeed;
      const s = conn.db.skyState.worldId.find(commons);
      if (s) { if (s.cycleSeconds > 0) cycle = s.cycleSeconds; skyWeather = s.weather; }
      held = (conn.db as unknown as { worldEnvironment?: { worldId: { find(id: bigint): { timeMode?: string } | undefined } } }).worldEnvironment?.worldId.find(commons)?.timeMode === 'fixed';
    } catch { /* tables mid-churn — the defaults stand */ }
    // The same precedence the renderer uses: the Local debug pin over the live-ops pin; 'clear' on the sky row is no pin.
    const debug = (this.app?.getModule('Weather') as { debugWeather?: string | null } | undefined)?.debugWeather ?? null;
    const commonsPin = skyWeather && skyWeather !== 'clear' ? skyWeather : undefined;
    const override = this.ignorePins ? undefined : pickWeatherOverride(debug, commonsPin);
    const result = forecast({
      seed, x: atlas.localX, z: atlas.localZ, nowSec: Date.now() / 1000, tod: currentTod(), cycleSeconds: cycle, heldClock: held,
      override, overrideBy: debug !== null ? 'debug' : 'office',
    });
    return { result, note: '' };
  }

  private draw(force: boolean): void {
    const host = this.tabEl;
    if (!host) return;
    if (!force && !host.isConnected) return;
    this.lastRender = performance.now();
    this.renders++;
    const { result, note } = this.compute();
    this.last = result; this.lastNote = note;
    softRender(host, (h) => this.build(h, result, note));
  }

  private build(h: HTMLElement, result: ForecastResult | null, note: string): void {
    h.appendChild(head('Weather'));
    if (!result) { h.appendChild(muted(note)); return; }
    if (result.held) {
      const w = KIND_WORDS[result.weather] ?? result.weather;
      const who = result.by === 'debug' ? 'pinned on this machine (debug)' : 'held by the weather office';
      h.appendChild(this.card(`${w} — ${who}`, 'The sky is set, not drifting: there is no forecast to give until the pin is lifted.'));
      return;
    }
    const warn = firstHazard(result.slots);
    const now = result.slots[0];
    if (now?.hazard) h.appendChild(this.warning(`${now.hazardWords} in force now — find shelter or a fire.`));
    else if (warn) h.appendChild(this.warning(`${warn.hazardWords} expected ${warn.label === '+1 h' ? 'within the hour' : `in ${warn.hours} hours`} — shelter, or a fire, will help.`));
    else h.appendChild(muted('No storm hazards in the next six hours.'));
    for (const s of result.slots) h.appendChild(this.row(s));
    h.appendChild(muted('A forecast of the natural field at where you stand; the weather moves with the wind, so a hundred metres rarely matters but a day\'s walk does.'));
  }

  private card(title: string, body: string): HTMLDivElement {
    const c = el('div', 'border:1px solid var(--ax-line2);border-radius:var(--ax-radius);background:var(--ax-bg2);padding:8px 10px;display:flex;flex-direction:column;gap:4px');
    c.appendChild(el('div', 'font-weight:600;color:var(--ax-text)', title));
    c.appendChild(el('div', 'color:var(--ax-muted);font-size:12px', body));
    return c;
  }

  private warning(text: string): HTMLDivElement {
    const w = el('div', 'border:1px solid var(--ax-bad);border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bad) 14%, var(--ax-bg2));color:var(--ax-text);padding:6px 10px;font-weight:600', `⚠ ${text}`);
    w.dataset.k = 'forecast-warning';
    return w;
  }

  private row(s: ForecastSlot): HTMLDivElement {
    const r = el('div', 'display:grid;grid-template-columns:54px 1fr;gap:4px 10px;align-items:baseline;border:1px solid var(--ax-line2);border-radius:var(--ax-radius);background:var(--ax-bg2);padding:7px 10px');
    r.dataset.hours = String(s.hours);
    r.appendChild(el('div', 'color:var(--ax-accent);font-weight:700', s.label));
    const kindText = `${KIND_WORDS[s.kind] ?? s.kind}${s.intensity > 0.02 ? ` · ${percentText(s.intensity)}` : ''}`;
    const top = el('div', 'color:var(--ax-text);font-weight:600', kindText);
    if (s.hazard) { const b = el('span', 'margin-left:8px;color:var(--ax-bad);font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.04em', `⚠ ${s.hazardWords}`); top.appendChild(b); }
    r.appendChild(top);
    r.appendChild(el('div'));
    r.appendChild(el('div', 'color:var(--ax-muted);font-size:12px', `${tempText(s.tempC)} · wind ${s.windWords.toLowerCase()} ${percentText(s.wind01)} · cloud ${percentText(s.cover01)}`));
    return r;
  }

  dispose(): void { this.tabEl = null; }
}
